<template>
  <div class="login-container">
    <div class="login-card">
      <div class="system-header">
        <img :src="logoUrl" class="system-logo" alt="系统logo"/>
        <h1 class="system-title">基于华为云的多模态抑郁症筛查及辅助治疗系统</h1>
      </div>

      <div class="role-selector">
        <button
            v-for="role in roles"
            :key="role.value"
            :class="[
                        'role-btn',
                        { active: selectedRole === role.value },
                    ]"
            @click="selectRole(role.value)"
        >
          {{ role.label }}
        </button>
      </div>

      <div class="form-container">
        <!-- 演示模式：输入框可见但禁用，无需输入账号密码 -->
        <div class="input-container">
          <i class="fas fa-user input-icon"></i>
          <input
              v-model="username"
              class="input-field"
              autocomplete="current-password"
              :placeholder="isMockMode ? '演示模式无需输入' : '请输入用户名'"
              :disabled="isMockMode"
              @keyup.enter="handleLogin"
          />
        </div>

        <div class="input-container">
          <i class="fas fa-lock input-icon"></i>
          <input
              v-model="password"
              autocomplete="current-password"
              type="password"
              class="input-field"
              :placeholder="isMockMode ? '演示模式无需输入' : '请输入密码'"
              :disabled="isMockMode"
              @keyup.enter="handleLogin"
          />
        </div>

        <div class="form-options">
          <!-- 左侧选项组：记住密码（或演示模式中徽标）+ 强制手机端复选框 -->
          <div class="options-left">
            <label v-if="!isMockMode" class="remember-me">
              <input type="checkbox" v-model="rememberPassword"/>
              <span>记住密码</span>
            </label>

            <!-- 勾选后病人登录无论设备类型都进入手机端页面（便于电脑上演示手机端 UI） -->
            <label class="remember-me force-mobile-label">
              <input
                  type="checkbox"
                  v-model="forceMobilePatient"
                  @change="handleForceMobileChange"
              />
              <span>强制进入病人手机端</span>
            </label>

            <!-- 演示模式：隐藏“记住密码”，改为显示演示提示 -->
            <span v-if="isMockMode" class="demo-mode-badge">演示模式中</span>
          </div>

          <!-- 演示模式下隐藏注册入口 -->
          <a
              v-if="!isMockMode"
              href="#"
              class="register-link"
              @click.prevent="handleRegister"
          >注册账号</a
          >
        </div>

        <button
            class="btn-login"
            :style="btnStyle"
            @click="handleLogin"
        >
          登 录
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {useUserStore} from "@/store";
import {IS_MOCK_MODE} from "@/mock";
import {ref, computed, onMounted} from "vue";
import {useRouter} from "vue-router";

export default {
  name: "LoginPage",
  setup() {
    const router = useRouter();
    const userStore = useUserStore();

    // 演示模式开关：由 .env 中的 MOCK_MODE 控制
    const isMockMode = IS_MOCK_MODE;

    // 如果已登录状态，直接跳转
    if (userStore.isLoggedIn) {
      router.push("/eeg-record");
    }

    // 响应式数据
    const username = ref("");
    const password = ref("");
    const rememberPassword = ref(false);
    const selectedRole = ref("doctor");
    // 强制进入病人手机端：勾选后病人登录无论设备类型都跳转 /m/main
    const forceMobilePatient = ref(false);

    // 角色选择：医生端没有手机版页面，选择医生时自动取消“强制进入病人手机端”
    const selectRole = (role) => {
      selectedRole.value = role;
      if (role === "doctor") {
        forceMobilePatient.value = false;
      }
    };

    // 勾选“强制进入病人手机端”时，自动切换为病人角色
    const handleForceMobileChange = () => {
      if (forceMobilePatient.value) {
        selectedRole.value = "patient";
      }
    };

    // 根据角色与设备/入口选择计算登录后的跳转路径
    const resolveRedirectPath = () => {
      if (userStore.role === "patient") {
        // 勾选强制手机端，或移动设备访问 → 病人手机端
        return forceMobilePatient.value || isMobile() ? "/m/main" : "/eeg-record";
      }
      // 医生统一进入医生桌面端
      return "/eeg-record";
    };

    // 角色定义
    const roles = [
      {value: "patient", label: "我是病人", color: "#10b981"},
      {value: "doctor", label: "我是医生", color: "#8b5cf6"},
    ];

    // Logo路径
    const logoUrl = require("@/assets/images/Xinjian-logo.png");

    // 计算登录按钮样式
    const btnStyle = computed(() => {
      const currentColor = roles.find(
          (r) => r.value === selectedRole.value
      ).color;
      return {
        background: `linear-gradient(135deg, ${currentColor}, ${currentColor}dd)`,
        boxShadow: `0 4px 12px ${currentColor}33`,
      };
    });

    // 计算当前主题色
    const currentColor = computed(() => {
      return roles.find((r) => r.value === selectedRole.value).color;
    });

    // 判断是否为手机端
    function isMobile() {
      return /Mobile|Android|iPhone|iPad|iPod|IEMobile|BlackBerry|Opera Mini/i.test(navigator.userAgent);
    }

    // 登录处理
    const handleLogin = async () => {
      try {
        // 演示模式下不输入账号密码，自动使用当前角色对应的演示账号
        // 医生演示账号：doctor；病人演示账号：patient
        const demoUsername = selectedRole.value === "doctor" ? "doctor" : "patient";

        // 等待登录和状态更新完成
        await userStore.login(
            selectedRole.value,
            {
              username: isMockMode ? demoUsername : username.value,
              password: isMockMode ? "demo-mode" : password.value, // 基本用户数据
            }
        );

        // 如果选择了记住密码（演示模式下无此选项）
        if (!isMockMode && rememberPassword.value) {
          localStorage.setItem("rememberedUsername", username.value);
          // 注意：实际项目中不应该直接存储密码，这里仅作演示
        }

        // 先根据用户角色，然后再根据设备类型/入口选择跳转（而不是根据设备类型先跳转）
        // 医生 → 医生端（桌面版）
        // 患者 + 勾选强制手机端或手机设备 → 患者手机端
        // 患者 + 电脑/平板 → 患者桌面端
        await router.push(resolveRedirectPath());
      } catch (error) {
        // console.error("登录出错:", error);
        alert("登录出错，请稍后再试");
      }
    };

    // 注册处理
    const handleRegister = () => {
      // 这里应该跳转到注册页面或打开注册弹窗
      alert(
          `即将跳转到${
              selectedRole.value === "doctor" ? "医生" : "病人"
          }注册页面`
      );
      // 或者跳转到注册页面
      // router.push('/register');
    };

// 加载记住的用户名
    const loadRememberedCredentials = () => {
      // 演示模式下不展示账号输入框，无需恢复记住的用户名
      if (isMockMode) {
        return;
      }
      const rememberedUsername =
          localStorage.getItem("rememberedUsername");
      if (rememberedUsername) {
        username.value = rememberedUsername;
        rememberPassword.value = true;
      }
    };

    // 组件挂载时加载记住的用户名
    onMounted(() => {
      loadRememberedCredentials();

      // 尝试从本地存储恢复登录状态
      const restored = userStore.loadFromLocalStorage();
      if (restored && userStore.isLoggedIn) {
        // 根据用户角色跳转
        router.push(resolveRedirectPath());
      }
    });

    return {
      username,
      password,
      rememberPassword,
      selectedRole,
      forceMobilePatient,
      roles,
      logoUrl,
      btnStyle,
      currentColor,
      isMockMode,
      selectRole,
      handleForceMobileChange,
      handleLogin,
      handleRegister,
    };
  },
};
</script>

<style scoped>
.login-container {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: url("@/assets/images/login-bng.jpg") no-repeat center;
  background-size: cover;
  position: relative;
}

.login-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(182, 182, 182, 0.5);
}

.login-card {
  position: relative;
  width: min(400px, 90%);
  padding: min(24px, 4vh) min(24px, 5vw);
  background: rgba(255, 255, 255, 0.92);
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(255, 255, 255, 0.1);
  max-height: 90vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.system-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
}

.system-logo {
  width: min(200px, 50vw);
  height: auto;
  margin-bottom: 8px;
}

.system-title {
  font-size: min(18px, 4vw);
  margin: 0;
  font-weight: 600;
  background: linear-gradient(135deg, #4f46e5, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
  word-break: break-word;
  line-height: 1.4;
}

.role-selector {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.role-btn {
  flex: 1;
  padding: 10px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #333;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;
}

.role-btn.active {
  color: white;
  background-color: var(--active-color, currentColor);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.role-btn:nth-child(1).active {
  --active-color: #10b981;
}

.role-btn:nth-child(2).active {
  --active-color: #8b5cf6;
}

/* 左侧选项组：多个选项横向排列 */
.options-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.force-mobile-label {
  font-size: 14px;
  color: #666;
}

.form-container {
  width: 100%;
}

.input-container {
  position: relative;
  margin-bottom: 20px;
}

.input-icon {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  color: #999;
  transition: color 0.3s;
}

.input-field {
  width: 100%;
  height: 46px;
  padding: 0 16px 0 42px;
  font-size: 15px;
  color: #333;
  border: 1px solid #ddd;
  border-radius: 23px;
  outline: none;
  transition: all 0.3s;
  box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
}

.input-field:focus {
  border-color: v-bind(currentColor);
  box-shadow: 0 0 0 4px v-bind('currentColor + "20"');
}

/* 演示模式下输入框禁用的外观 */
.input-field:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
  color: #999;
}

.input-container:focus-within .input-icon {
  color: v-bind(currentColor);
}

.remember-me {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  user-select: none;
}

/* 演示模式提示徽标（替代“记住密码”的位置） */
.demo-mode-badge {
  display: inline-flex;
  align-items: center;
  font-size: 14px;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.35);
  border-radius: 6px;
  padding: 3px 10px;
  user-select: none;
}

.remember-me input {
  position: relative;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  appearance: none;
  border: 2px solid #ddd;
  border-radius: 4px;
  outline: none;
  transition: all 0.3s;
  cursor: pointer;
}

.remember-me input:checked {
  background-color: v-bind(currentColor);
  border-color: v-bind(currentColor);
}

.remember-me input:checked::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 5px;
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.btn-login {
  width: 100%;
  height: 46px;
  font-size: 17px;
  color: #fff;
  border: none;
  border-radius: 23px;
  cursor: pointer;
  transition: opacity 0.2s;
  margin-top: 16px;
}

.btn-login:hover {
  opacity: 0.9;
}

.btn-login:active {
  opacity: 0.8;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.register-link {
  font-size: 14px;
  color: v-bind(currentColor);
  text-decoration: none;
  transition: opacity 0.2s;
}

.register-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}
</style>
